/// <amd-module name="Repos/Tfvc/Views/FilesHub/Components/CompareContent" />
import * as React from "react";
import { ChangesContext, IChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { getTfvcNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";
import { IDiffNavigation } from "Repos/ChangesCommon/Contracts";
import { IDiffViewerProps } from "Repos/Common/Contracts/Editor";
import { TfvcVersionDescriptor, TfvcVersionOption, TfvcVersionType } from "Repos/Common/Contracts/Git";
import { IChangesetsHistoryViewProps } from "Repos/Common/Contracts/History";
import { getTfvcVersionString, tfvcVersionStringToVersionDescriptor } from "Repos/Common/Util/Version";
import { IGitItemContent } from "Repos/Files/Contracts";
import { IFilesHubFilterActionsService } from "Repos/FilesHubCommon/FilesHubFilterActionsService";
import { IFilesHubTabsService } from "Repos/FilesHubCommon/FilesHubTabs";
import * as Resources from "Repos/Tfvc/Views/FilesHub/Resources";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { ExpandableButton } from "VSSUI/Button";
import { Callout } from "VSSUI/Callout";
import { Card } from "VSSUI/Card";
import { IExpandable } from "VSSUI/Expandable";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { Pill } from "VSSUI/Pill";
import { ResponsiveLayout } from "VSSUI/ResponsiveLayout";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { IOffset, IOrigin, IPoint, Location } from "VSSUI/Utilities/Position";
import { getFilesHubTabsService, getFilesHubViewService, IFilesHubViewService, registerFilesHubTab } from "../FilesHubViewService";

interface ICompareVersion {
    content: IReadonlyObservableValue<IGitItemContent | undefined>;
    path: string;
    version: TfvcVersionDescriptor;
}

export class CompareContent extends VssComponent<{}> {
    private modifiedCompareVersion: ICompareVersion;
    private originalCompareVersion: ICompareVersion;

    public render() {
        const filesService = getFilesHubViewService(this.context.pageContext);
        const compareService = this.context.pageContext.getService<ITfvcCompareContentService>("ITfvcCompareContentService");

        return (
            <PerformanceScenarioContext.Consumer>
                {scenarioProps => {
                    scenarioProps.getScenario()!.addProperties({ editor: true });
                    const pendingWork = scenarioProps.registerPendingWork("load-file-diff-viewer");
                    return (
                        <Observer
                            modifiedPath={compareService.getModifiedPath()}
                            modifiedVersion={compareService.getModifiedVersion()}
                            originalPath={compareService.getOriginalPath()}
                            originalVersion={compareService.getOriginalVersion()}
                        >
                            {(versionProps: {
                                modifiedPath: string;
                                modifiedVersion: TfvcVersionDescriptor;
                                originalPath: string;
                                originalVersion: TfvcVersionDescriptor;
                            }) => {
                                if (
                                    !this.modifiedCompareVersion ||
                                    this.modifiedCompareVersion.path !== versionProps.modifiedPath ||
                                    getTfvcVersionString(this.modifiedCompareVersion.version) !== getTfvcVersionString(versionProps.modifiedVersion)
                                ) {
                                    this.modifiedCompareVersion = {
                                        content: filesService.getItemContent(versionProps.modifiedPath, versionProps.modifiedVersion),
                                        path: versionProps.modifiedPath,
                                        version: versionProps.modifiedVersion
                                    };
                                }

                                if (
                                    !this.originalCompareVersion ||
                                    this.originalCompareVersion.path !== versionProps.originalPath ||
                                    getTfvcVersionString(this.originalCompareVersion.version) !== getTfvcVersionString(versionProps.originalVersion)
                                ) {
                                    this.originalCompareVersion = {
                                        content: filesService.getItemContent(versionProps.originalPath, versionProps.originalVersion),
                                        path: versionProps.originalPath,
                                        version: versionProps.originalVersion
                                    };
                                }

                                return (
                                    <>
                                        <CompareHeader
                                            modifiedVersion={this.modifiedCompareVersion}
                                            originalVersion={this.originalCompareVersion}
                                            onModifiedVersionChanged={(version, path) => {
                                                compareService.setModifiedVersion(version, path);
                                            }}
                                            onOriginalVersionChanged={(version, path) => {
                                                compareService.setOriginalVersion(version, path);
                                            }}
                                            pageContext={this.context.pageContext}
                                        />
                                        <CompareDiffContent
                                            modifiedVersion={this.modifiedCompareVersion}
                                            originalVersion={this.originalCompareVersion}
                                            pageContext={this.context.pageContext}
                                            onLoaded={pendingWork && pendingWork.complete}
                                        />
                                    </>
                                );
                            }}
                        </Observer>
                    );
                }}
            </PerformanceScenarioContext.Consumer>
        );
    }
}

interface ICompareDiffContentProps {
    modifiedVersion: ICompareVersion;
    originalVersion: ICompareVersion;
    pageContext: IVssPageContext;
    onLoaded?: () => void;
}

const CompareDiffContent = (props: ICompareDiffContentProps): JSX.Element => {
    const filesService = getFilesHubViewService(props.pageContext);
    const navigationService = getTfvcNavigationService(props.pageContext);
    return (
        <Observer originalContent={props.originalVersion.content} modifiedContent={props.modifiedVersion.content}>
            {(contentProps: { originalContent: IGitItemContent | undefined; modifiedContent: IGitItemContent | undefined }) => {
                const getModifiedUrl = () => filesService.getFileDownloadUrl(props.modifiedVersion.path, props.modifiedVersion.version);
                let getOriginalUrl = () => filesService.getFileDownloadUrl(props.originalVersion.path, props.originalVersion.version);

                const modifiedContent = contentProps.modifiedContent;
                let originalContent = contentProps.originalContent;
                if (originalContent && originalContent.error) {
                    // There is no previous version for this item.
                    originalContent = modifiedContent;
                    getOriginalUrl = getModifiedUrl;
                }

                const nonBinaryContent = contentProps.modifiedContent && !contentProps.modifiedContent.details.metadata.isBinary;

                return (
                    <ChangesContext.Provider value={{ navigationService } as IChangesContext<TfvcVersionDescriptor>}>
                        <Card
                            className={css("no-padding", nonBinaryContent && "flex-grow flex-noshrink v-scroll-hidden")}
                            contentProps={{ className: "relative", contentPadding: false }}
                        >
                            {originalContent && modifiedContent ? (
                                <div className={css("flex-row scroll-hidden", nonBinaryContent && "absolute-fill")}>
                                    <WrappedComponent<IDiffViewerProps>
                                        wrappedType="repos-diff-viewer"
                                        dependencies={["ms.vss-code-web.code-editor"]}
                                        wrappedProps={{
                                            addDiffKeyboardShortcuts: false,
                                            className: "flex-row flex-grow",
                                            diffOrientation: filesService.getDiffOrientation(),
                                            editable: false,
                                            editorClassName: "repos-file-editor-background scroll-hidden",
                                            getModifiedDownloadUrl: getModifiedUrl,
                                            getOriginalDownloadUrl: getOriginalUrl,
                                            isPixelDiff: false,
                                            modifiedContent: modifiedContent.details,
                                            onLoaded: props.onLoaded,
                                            originalContent: originalContent.details
                                        }}
                                    />
                                </div>
                            ) : (
                                <div className="repos-file-editor-background flex-grow" />
                            )}
                        </Card>
                    </ChangesContext.Provider>
                );
            }}
        </Observer>
    );
};

interface ICompareHeaderProps {
    modifiedVersion: ICompareVersion;
    originalVersion: ICompareVersion;
    pageContext: IVssPageContext;
    onModifiedVersionChanged: (newVersion: TfvcVersionDescriptor, path?: string) => void;
    onOriginalVersionChanged: (newVersion: TfvcVersionDescriptor, path?: string) => void;
}

const CompareHeader = (props: ICompareHeaderProps): JSX.Element => {
    const filesService = getFilesHubViewService(props.pageContext);
    const navigationService = getTfvcNavigationService(props.pageContext);

    function getHeaderItems(): IHeaderCommandBarItem[] {
        return [
            {
                ariaLabel: Resources.PreviousDiff,
                disabled: !navigationService.getHasPreviousDiff(),
                iconProps: { iconName: "Up" },
                id: "prev-diff",
                important: true,
                onActivate: () => {
                    navigationService.previousDiff();
                },
                subtle: true
            },
            {
                ariaLabel: Resources.NextDiff,
                disabled: !navigationService.getHasNextDiff(),
                iconProps: { iconName: "Down" },
                id: "next-diff",
                important: true,
                onActivate: () => {
                    navigationService.nextDiff();
                },
                subtle: true
            },
            filesService.getDiffOrientationMenuItem()
        ];
    }

    return (
        <Card className="no-padding" contentProps={{ contentPadding: false, className: "repos-file-compare-toolbar" }}>
            <div className="rhythm-horizontal-8 flex-noshrink margin-left-8 margin-vertical-8">
                <CompareVersionDropdown
                    pageContext={props.pageContext}
                    currentVersion={props.originalVersion}
                    onVersionChanged={props.onOriginalVersionChanged}
                    ariaLabelPrefix="Original"
                />
                <CompareVersionDropdown
                    pageContext={props.pageContext}
                    currentVersion={props.modifiedVersion}
                    onVersionChanged={props.onModifiedVersionChanged}
                    ariaLabelPrefix="Modified"
                />
            </div>
            <Observer diffNavigation={navigationService.getDiffNavigation()} diffOrientation={filesService.getDiffOrientation()}>
                {({ diffNavigation }: { diffNavigation: IDiffNavigation | undefined }) => {
                    return (
                        <ResponsiveLayout responsiveChildren={[1, 2]} ignoredChildren={[0]}>
                            <div className="flex-row flex-center flex-grow rhythm-horizontal-8 margin-8 scroll-hidden">
                                <div className="flex-grow" key="mid-spacer" />
                                <div className="flex-row" key="added-removed-pills">
                                    {diffNavigation && diffNavigation.totalRemovedLines > 0 && (
                                        <Pill containsCount={true} className="repos-compare-removed-lines-pill">
                                            <Tooltip text={format(Resources.LinesRemovedTooltip, diffNavigation.totalRemovedLines)}>
                                                <span>-{diffNavigation.totalRemovedLines}</span>
                                            </Tooltip>
                                        </Pill>
                                    )}
                                    {diffNavigation && diffNavigation.totalAddedLines > 0 && (
                                        <Pill containsCount={true} className="repos-compare-added-lines-pill">
                                            <Tooltip text={format(Resources.LinesAddedTooltip, diffNavigation.totalAddedLines)}>
                                                <span>+{diffNavigation.totalAddedLines}</span>
                                            </Tooltip>
                                        </Pill>
                                    )}
                                </div>
                                <HeaderCommandBar className="repos-compare-header-commandbar" items={getHeaderItems()} key="diff-header" />
                            </div>
                        </ResponsiveLayout>
                    );
                }}
            </Observer>
        </Card>
    );
};

interface ICompareVersionDropdownProps {
    pageContext: IVssPageContext;
    currentVersion: ICompareVersion;
    onVersionChanged: (newVersion: TfvcVersionDescriptor, path: string) => void;
    ariaLabelPrefix: string;
}

const CompareVersionDropdown = (props: ICompareVersionDropdownProps): JSX.Element => {
    return (
        <Observer content={props.currentVersion.content}>
            {(contentProps: { content: IGitItemContent | undefined }) => {
                const filesService = getFilesHubViewService(props.pageContext);
                const version = props.currentVersion.version;

                let commitId = "";
                let decoratorText = "";
                let ariaLabelText = "";

                if (contentProps.content && contentProps.content.error && version.versionOption === TfvcVersionOption.Previous) {
                    decoratorText = Resources.NoPreviousVersion;
                    ariaLabelText = Resources.NoPreviousVersion;
                } else {
                    if (contentProps.content) {
                        commitId = contentProps.content.commitId;
                    } else {
                        commitId = version.version;
                    }

                    ariaLabelText = props.ariaLabelPrefix + " " + commitId;

                    if (version.versionType === TfvcVersionType.Latest) {
                        if (version.versionOption === TfvcVersionOption.None) {
                            decoratorText = Resources.Latest;
                        } else if (version.versionOption === TfvcVersionOption.Previous) {
                            decoratorText = Resources.Previous;
                        }

                        ariaLabelText = ariaLabelText + " " + decoratorText;
                    }
                }

                return (
                    <ExpandableButton
                        anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                        dropdownOrigin={{ horizontal: Location.start, vertical: Location.start }}
                        renderCallout={(
                            dropdown: IExpandable,
                            dropdownId: string,
                            anchorElement?: HTMLElement,
                            anchorOffset?: IOffset,
                            anchorOrigin?: IOrigin,
                            anchorPoint?: IPoint,
                            dropdownOrigin?: IOrigin
                        ): JSX.Element => {
                            return (
                                <Callout
                                    anchorElement={anchorElement}
                                    anchorOffset={anchorOffset}
                                    anchorOrigin={anchorOrigin}
                                    anchorPoint={anchorPoint}
                                    blurDismiss={true}
                                    calloutOrigin={dropdownOrigin}
                                    contentShadow={true}
                                    escDismiss={true}
                                    focuszoneProps={{
                                        circularNavigation: true,
                                        defaultActiveElement: ".repos-compare-version-dropdown",
                                        focusOnMount: true
                                    }}
                                    lightDismiss={true}
                                    onDismiss={dropdown.collapse}
                                >
                                    <div
                                        className="repos-compare-version-dropdown v-scroll-auto custom-scrollbar scroll-auto-hide no-outline"
                                        tabIndex={-1}
                                    >
                                        <WrappedComponent<IChangesetsHistoryViewProps>
                                            wrappedType="repos-changesets-history"
                                            dependencies={["ms.vss-code-web.changesets-list"]}
                                            wrappedProps={{
                                                filePath: filesService.getSelectedItem().value.path,
                                                filterVisible: false,
                                                onActivate: (event, changeset) => {
                                                    event.preventDefault();
                                                    props.onVersionChanged({
                                                        version: changeset.changesetId.toFixed(),
                                                        versionType: TfvcVersionType.Changeset,
                                                        versionOption: TfvcVersionOption.None
                                                    }, changeset.serverItem);
                                                    dropdown.collapse();
                                                },
                                                shouldUpdateUrl: false,
                                                showHeader: false,
                                                version: filesService.getVersion()
                                            }}
                                        />
                                    </div>
                                </Callout>
                            );
                        }}
                        subtle={true}
                        ariaLabel={ariaLabelText}
                    >
                        <>
                            {commitId && <span className="font-weight-semibold margin-right-4">{commitId}</span>}
                            {decoratorText && <span className="secondary-text">{"(" + decoratorText + ")"}</span>}
                        </>
                    </ExpandableButton>
                );
            }}
        </Observer>
    );
};

function getPreviousVersion(version: TfvcVersionDescriptor): TfvcVersionDescriptor {
    return { ...version, versionOption: TfvcVersionOption.Previous };
}

interface ITfvcCompareContentService extends IVssService {
    getModifiedPath(): IReadonlyObservableValue<string>;
    getModifiedVersion(): IReadonlyObservableValue<TfvcVersionDescriptor>;
    getOriginalPath(): IReadonlyObservableValue<string>;
    getOriginalVersion(): IReadonlyObservableValue<TfvcVersionDescriptor>;
    setModifiedVersion(version: TfvcVersionDescriptor, path?: string): void;
    setOriginalVersion(version: TfvcVersionDescriptor, path?: string): void;
}

class CompareContentService extends VssService implements ITfvcCompareContentService {
    private modifiedPath: ObservableValue<string>;
    private modifiedVersion: ObservableValue<TfvcVersionDescriptor>;
    private originalPath: ObservableValue<string>;
    private originalVersion: ObservableValue<TfvcVersionDescriptor>;

    private filesService: IFilesHubViewService;
    private tabsService: IFilesHubTabsService;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.filesService = getFilesHubViewService(this.pageContext);
        this.tabsService = getFilesHubTabsService(this.pageContext);

        const selectedItemPath = this.filesService.getSelectedItem().value.path;
        const pageHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        const historyState = pageHistoryService.getProjectedState();

        this.modifiedPath = new ObservableValue(historyState["mpath"] || selectedItemPath);
        pageHistoryService.registerObservable(
            this.modifiedPath,
            "mpath",
            HistoryBehavior.replace,
            value => (value === this.filesService.getSelectedItem().value.path ? "" : value),
            fromUrl => fromUrl || this.filesService.getSelectedItem().value.path
        );

        this.originalPath = new ObservableValue(historyState["opath"] || selectedItemPath);
        pageHistoryService.registerObservable(
            this.originalPath,
            "opath",
            HistoryBehavior.replace,
            value => (value === this.filesService.getSelectedItem().value.path ? "" : value),
            fromUrl => fromUrl || this.filesService.getSelectedItem().value.path
        );

        this.modifiedVersion = new ObservableValue(this.filesService.getVersion());
        pageHistoryService.registerObservable(
            this.modifiedVersion,
            "mversion",
            HistoryBehavior.replace,
            version => {
                const currentVersion = getTfvcVersionString(this.filesService.getVersion());
                const versionString = getTfvcVersionString(version);
                return versionString === currentVersion ? "" : versionString;
            },
            fromUrl => (fromUrl ? tfvcVersionStringToVersionDescriptor(fromUrl) : this.filesService.getVersion())
        );

        this.originalVersion = new ObservableValue(getPreviousVersion(this.filesService.getVersion()));
        pageHistoryService.registerObservable(
            this.originalVersion,
            "oversion",
            HistoryBehavior.replace,
            version => {
                const currentPrevVersion = getTfvcVersionString(getPreviousVersion(this.filesService.getVersion()));
                const versionString = getTfvcVersionString(version);
                return versionString === currentPrevVersion ? "" : versionString;
            },
            fromUrl => (fromUrl ? tfvcVersionStringToVersionDescriptor(fromUrl) : getPreviousVersion(this.filesService.getVersion()))
        );

        this.filesService.getSelectedItem().subscribe(this.clearCustomVersions);
        this.tabsService.getSelectedTabId().subscribe(this.clearCustomVersions);
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);

        this.filesService.getSelectedItem().unsubscribe(this.clearCustomVersions);
        this.tabsService.getSelectedTabId().unsubscribe(this.clearCustomVersions);
    }

    public getModifiedPath(): IReadonlyObservableValue<string> {
        return this.modifiedPath;
    }

    public getModifiedVersion(): IReadonlyObservableValue<TfvcVersionDescriptor> {
        return this.modifiedVersion;
    }

    public getOriginalPath(): IReadonlyObservableValue<string> {
        return this.originalPath;
    }

    public getOriginalVersion(): IReadonlyObservableValue<TfvcVersionDescriptor> {
        return this.originalVersion;
    }

    public setModifiedVersion(version: TfvcVersionDescriptor, path?: string): void {
        this.modifiedVersion.value = version;

        if (!path) {
            const filesService = getFilesHubViewService(this.pageContext);
            path = filesService.getSelectedItem().value.path;
        }

        if (path !== this.modifiedPath.value) {
            this.modifiedPath.value = path;
        }
    }

    public setOriginalVersion(version: TfvcVersionDescriptor, path?: string): void {
        this.originalVersion.value = version;

        if (!path) {
            const filesService = getFilesHubViewService(this.pageContext);
            path = filesService.getSelectedItem().value.path;
        }

        if (path !== this.originalPath.value) {
            this.originalPath.value = path;
        }
    }

    private clearCustomVersions = (): void => {
        const selectedItemPath = this.filesService.getSelectedItem().value.path;
        const version = this.filesService.getVersion();

        this.modifiedPath.value = selectedItemPath;
        this.modifiedVersion.value = version;
        this.originalPath.value = selectedItemPath;
        this.originalVersion.value = getPreviousVersion(version);
    };
}

Services.add("ITfvcCompareContentService", { serviceFactory: CompareContentService });

registerFilesHubTab({
    getTab: (pageContext, item, editMode) => {
        if (!item.isFolder && !item.isInvalid && !editMode) {
            const actionService = pageContext.getService<IFilesHubFilterActionsService>("IFilesHubFilterActionsService");

            return {
                id: "compare",
                name: Resources.Compare,
                order: 30,
                render: () => <CompareContent />,
                renderFilterBar: (inTabBar: boolean) => {
                    return inTabBar ? <HeaderCommandBar items={actionService.getActions()} /> : undefined;
                }
            };
        }
    },
    dependencies: ["ms.vss-code-web.code-editor"]
});
